/**
 * Model selection logic for smart preloading
 * V1.4: Smart Preload - Best Fit Model Selection
 *
 * This module determines which model to preload based on:
 * - Device capabilities (memory, GPU)
 * - Already cached models
 * - User preference (speed vs quality)
 */
/**
 * Select the best model to preload based on device and strategy.
 *
 * Logic:
 * 1. If any model is already cached → return that (no download needed)
 * 2. Filter models that fit device memory (with safety margin)
 * 3. Score remaining models based on strategy
 * 4. Return highest scoring model
 */
export function selectPreloadModel(input) {
    const { device, models, cachedModelIds, strategy } = input;
    // Step 1: Check if we already have a cached model
    const cachedModels = models.filter((m) => cachedModelIds.includes(m.id));
    if (cachedModels.length > 0) {
        // Find the best cached model (prefer quality tier)
        const bestCached = cachedModels.sort((a, b) => {
            const tierOrder = { quality: 3, balanced: 2, fast: 1 };
            const aTier = tierOrder[a.tier || 'balanced'] || 2;
            const bTier = tierOrder[b.tier || 'balanced'] || 2;
            return bTier - aTier;
        })[0];
        return {
            selectedModel: null,
            reason: `Model "${bestCached.id}" already cached`,
            skipped: true,
            existingCachedModel: bestCached,
        };
    }
    // Step 2: Filter by device capability
    // Use recommendedMaxModelSizeMB as the primary source, with fallback to device memory
    const deviceMemoryMB = (device.memory?.deviceMemoryGB || 4) * 1024;
    const safetyMargin = 0.7; // Use only 70% of available memory
    const maxModelSizeMB = device.inferenceHints.recommendedMaxModelSizeMB || (deviceMemoryMB * safetyMargin);
    const fittingModels = models.filter((m) => {
        const sizeMB = m.sizeMB || 500;
        return sizeMB <= maxModelSizeMB;
    });
    if (fittingModels.length === 0) {
        return {
            selectedModel: null,
            reason: `No models fit device memory (${Math.round(maxModelSizeMB)}MB available)`,
            skipped: true,
        };
    }
    // Step 3: Score models based on strategy
    const scoredModels = fittingModels.map((model) => ({
        model,
        score: scoreModel(model, device, strategy, maxModelSizeMB),
    }));
    // Sort by score descending
    scoredModels.sort((a, b) => b.score - a.score);
    const selected = scoredModels[0];
    return {
        selectedModel: selected.model,
        reason: `Selected "${selected.model.id}" (score: ${selected.score.toFixed(2)}, strategy: ${strategy})`,
        skipped: false,
    };
}
/**
 * Score a model based on strategy and device fit.
 */
function scoreModel(model, device, strategy, maxSizeMB) {
    const sizeMB = model.sizeMB || 500;
    const tier = model.tier || 'balanced';
    // Base quality score from tier
    const tierScores = { fast: 1, balanced: 2, quality: 3 };
    const qualityScore = tierScores[tier] || 2;
    // Size score (larger = potentially better quality)
    // Normalized to 0-1 based on max size we can handle
    const sizeScore = Math.min(sizeMB / maxSizeMB, 1);
    // Headroom score (more headroom = safer)
    const headroom = (maxSizeMB - sizeMB) / maxSizeMB;
    // GPU tier bonus - infer from recommended model size
    // High-end: can handle 2GB+ models, Mid: 1-2GB, Low: <1GB
    const recommendedMax = device.inferenceHints.recommendedMaxModelSizeMB;
    const gpuBonus = recommendedMax >= 2000 ? 0.2 : (recommendedMax >= 1000 ? 0.1 : 0);
    // Strategy weighting
    let finalScore;
    switch (strategy) {
        case 'speed':
            // Prefer smaller, faster models
            finalScore = (1 - sizeScore) * 2 + headroom + (tier === 'fast' ? 1 : 0);
            break;
        case 'quality':
            // Prefer larger, higher quality models (that still fit)
            finalScore = qualityScore * 2 + sizeScore + gpuBonus;
            break;
        case 'balanced':
        default:
            // Balance between quality and safety margin
            finalScore = qualityScore + sizeScore * 0.5 + headroom * 0.5 + gpuBonus;
            break;
    }
    return finalScore;
}
/**
 * Get recommended strategy based on device capabilities.
 */
export function getRecommendedStrategy(device) {
    const memoryGB = device.memory?.deviceMemoryGB || 4;
    const recommendedMax = device.inferenceHints.recommendedMaxModelSizeMB;
    // High-end: 16GB+ RAM and can handle 2GB+ models
    if (memoryGB >= 16 && recommendedMax >= 2000) {
        return 'quality';
    }
    // Mid-range: 8GB+ RAM or can handle 1GB+ models
    if (memoryGB >= 8 || recommendedMax >= 1000) {
        return 'balanced';
    }
    // Low-end: everything else
    return 'speed';
}
/**
 * Check if we should skip preloading based on connection quality.
 * Uses navigator.connection API when available.
 */
export function shouldSkipForConnection() {
    if (typeof navigator === 'undefined')
        return false;
    const connection = navigator.connection;
    if (!connection)
        return false;
    // Skip on 2G or slow-2g
    if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
        return true;
    }
    // Skip if data saver is enabled
    if (connection.saveData) {
        return true;
    }
    return false;
}
//# sourceMappingURL=selectPreloadModel.js.map